import React, { useCallback, useMemo, useRef, useState } from 'react';
import { zip } from 'fflate';
import {
  Upload, Download, X, Image as ImageIcon, Loader2, CheckCircle2,
  AlertCircle, Sparkles, Blocks,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// ── Çıktı formatları — e-ticaret için 3:4 varsayılan ─────────────────────────
const FORMATS = [
  {
    id: 'portrait', label: 'Dikey 3:4 (e-ticaret)', aspect: '3:4', default: true,
    hint: 'Vertical 3:4 e-commerce product frame. Center the toy in the frame and let it occupy roughly 70–80% of the height, ' +
      'with even, comfortable margins on all four sides. The ENTIRE product must be inside the frame — never crop or cut off ' +
      'any part of it. Shoot from a slightly elevated three-quarter angle so the form and depth of the toy read clearly.',
  },
  {
    id: 'square', label: 'Kare 1:1', aspect: '1:1',
    hint: 'Balanced, symmetrical 1:1 composition with the toy centered and fully inside the frame, with even margins. ' +
      'Never crop any part of the product.',
  },
  {
    id: 'wide', label: 'Yatay 4:3', aspect: '4:3',
    hint: 'Horizontal 4:3 composition; place the toy slightly off-center following the rule of thirds and keep the ' +
      'opposite side calm and uncluttered so marketing text can be added later. The whole product stays inside the frame.',
  },
];

// ── Ahşap oyuncak / e-ticaret sahne preset'leri ──────────────────────────────
const THEMES = [
  {
    id: 'white', emoji: '⬜', label: 'Beyaz Stüdyo (pazaryeri)',
    prompt: 'A clean pure-white seamless e-commerce studio background (#ffffff) with no visible horizon line, corner or wall ' +
      'edge, the toy standing or resting naturally on the surface. Light it with a professional two-softbox setup plus a ' +
      'fill card: bright, even, neutral 5500K light with no colour cast, no harsh hotspots, no blown-out white patches on ' +
      'the wood, and gentle soft-edged modelling shadows that still reveal the form and the grain. Add a subtle, ' +
      'realistic contact shadow directly under the product so it is grounded and not floating. The white must read as ' +
      'pure clean white, and the toy must separate crisply from it with a clean, well-defined edge — never a muddy grey ' +
      'halo, never a cut-out look. No props, no decoration, no gradient, no vignette, no reflective floor — this is a ' +
      'marketplace catalogue shot (Trendyol / Amazon style) where the product is the only subject.',
  },
  {
    id: 'softgray', emoji: '🩶', label: 'Açık Gri Gradyan',
    prompt: 'A smooth, seamless light-grey studio backdrop with a very gentle top-to-bottom gradient, soft diffused studio ' +
      'lighting and a natural soft shadow beneath the toy. Minimal, modern, premium catalogue look with no props.',
  },
  {
    id: 'woodfloor', emoji: '🪵', label: 'Ahşap Zemin + Doğal Işık',
    prompt: 'The toy sits naturally on a warm light oak wooden floor or table, lit by soft natural daylight coming through a ' +
      'window from one side, with gentle realistic shadows and a hint of warm sunlight. The background is a softly blurred ' +
      'bright neutral interior wall. Warm, honest, natural-materials mood that shows off the real wood grain.',
  },
  {
    id: 'nursery', emoji: '🧸', label: 'Çocuk Odası / Nursery',
    prompt: 'A bright, tidy Scandinavian children\'s room: soft cream and pale wood tones, a light rug, a plain painted wall, ' +
      'a softly blurred low shelf and a linen curtain in the background. The toy is placed naturally on the rug or on a low ' +
      'wooden table, in soft diffused daylight. Calm, warm, uncluttered family-home atmosphere — a few gentle background ' +
      'elements only, all well out of focus so the toy stays the clear hero.',
  },
  {
    id: 'montessori', emoji: '📚', label: 'Montessori Raf',
    prompt: 'The toy is displayed on a low, open, natural-birch Montessori shelf against a plain warm-white wall. Neutral, ' +
      'muted, educational-play aesthetic with soft even daylight. The shelf is deliberately sparse — at most one or two ' +
      'blurred neutral items far in the background. Clean, intentional, calm learning-environment mood.',
  },
  {
    id: 'playmat', emoji: '🧩', label: 'Oyun Halısı (üstten)',
    prompt: 'A flat-lay, straight top-down view: the toy laid out neatly on a soft cream or oatmeal play mat / knitted ' +
      'blanket, in bright soft diffused daylight with gentle shadows. Tidy, airy, styled-from-above composition with ' +
      'generous clean negative space around the product.',
  },
  {
    id: 'scandi', emoji: '🕊️', label: 'İskandinav Minimal',
    prompt: 'A minimal Scandinavian still life: a plain off-white or warm beige plaster background, a pale wooden or ' +
      'textured stone surface, soft directional daylight casting a long gentle shadow. Restrained, tasteful, editorial ' +
      'lifestyle look with lots of quiet negative space and no distracting props.',
  },
  {
    id: 'nature', emoji: '🌿', label: 'Doğal / Sürdürülebilir',
    prompt: 'A natural, eco-conscious still life: the toy resting on raw linen fabric or a light wooden board, with a few ' +
      'small fresh green leaves or a dried branch nearby, warm soft daylight and organic earthy tones. The styling stays ' +
      'simple and honest, communicating natural untreated wood and sustainable craftsmanship.',
  },
  {
    id: 'gift', emoji: '🎁', label: 'Hediye / Yılbaşı',
    prompt: 'A warm festive gift scene: soft golden bokeh string lights well out of focus in the background, a few pine ' +
      'sprigs and a simple kraft-paper or satin ribbon detail near the toy, cozy warm lighting. Inviting gift-season mood ' +
      'that still keeps the toy clearly readable as the product.',
  },
  {
    id: 'macro', emoji: '🔍', label: 'Detay Makro (ahşap dokusu)',
    prompt: 'A close-up macro detail shot that highlights the craftsmanship: the real wood grain, the smooth sanded rounded ' +
      'edges, the joinery and the painted details of the toy, with soft raking side light and a very shallow depth of field. ' +
      'The background is a softly blurred neutral warm surface. Tactile, premium, artisanal feel.',
  },
];

// ── Ürün kimliği: her seviyede geçerli, tartışılmaz kurallar ────────────────
const IDENTITY_RULES =
  'You are a high-end commercial product photographer shooting an e-commerce catalogue for a wooden children\'s toy brand. ' +
  'The attached image is a REAL physical product. ' +
  'PRODUCT IDENTITY (never change): the shape and silhouette, the proportions, the number and arrangement of parts, the ' +
  'wood tone and grain, the painted colours, and every printed shape, letter, number, face or logo must match the real ' +
  'product exactly. Do NOT redesign, restyle, recolour or simplify it. Do NOT invent extra pieces, extra blocks, extra ' +
  'figures or a second copy of the product. Do NOT add any text, brand name, label, sticker or packaging that is not ' +
  'already on the real product. A customer must receive exactly the toy they see here. ' +
  'MATERIAL TRUTH: render real natural wood — visible grain, matte or lightly oiled finish, softly rounded child-safe ' +
  'edges. Never make it look like glossy plastic, metal or CGI. ' +
  'SCALE: this is a handheld children\'s toy, typically about 10–40 cm. Render it at a realistic, true-to-life size ' +
  'relative to every other object in the frame. ' +
  'E-COMMERCE RULES: the complete product must be fully visible and never cropped by the frame edge. Keep it in sharp ' +
  'focus from front to back so every detail is legible, sitting on a believable surface with a natural contact shadow — ' +
  'never floating, never balanced on an artificial pedestal or stack. ' +
  'Output a single image.';

// ── İyileştirme seviyeleri ──────────────────────────────────────────────────
const LEVELS = [
  {
    id: 'faithful', label: 'Sadık', temp: 0.35, hint: 'Referansa en yakın — mevcut çekimi neredeyse aynen korur',
    prompt:
      'TREATMENT — FAITHFUL: stay as close to the reference photograph as possible. Keep the same camera angle, the same ' +
      'pose and the same framing; only replace the background and lighting with the scene described below.',
  },
  {
    id: 'improve', label: 'Düzelt (önerilen)', temp: 0.55, hint: 'Aynı ürün, profesyonel yeniden çekim — ışık, duruş, temizlik düzeltilir',
    prompt:
      'TREATMENT — PROFESSIONAL RE-SHOOT. This is the most important instruction: the reference image defines ONLY what ' +
      'the product IS. It does NOT define how the photo should look. Do NOT copy the reference photograph. The reference ' +
      'is very likely an amateur snapshot, and you must NOT reproduce its flaws. ' +
      'Re-photograph this same toy properly, in a professional studio, as if the brand hired you to redo the shoot. ' +
      'ACTIVELY FIX every technical problem in the reference: replace dull, yellow, uneven or mixed-colour-temperature ' +
      'lighting with clean neutral studio light; remove harsh dark shadows and blown-out highlights; remove noise, ' +
      'grain, motion blur and softness, and render the toy tack-sharp and high-resolution; straighten a crooked or ' +
      'tilted product so it sits perfectly level and upright; correct wide-angle, keystone or perspective distortion so ' +
      'proportions read true; centre and re-frame the composition with clean even margins. ' +
      'CLEAN THE PRODUCT: remove dust, lint, fingerprints, smudges, glue residue, stray threads, price stickers, ' +
      'reflections of the room and any clutter or background objects from the reference. Present the toy as a pristine, ' +
      'brand-new retail unit — but keep the genuine wood grain, the real paint finish and any intentional design detail. ' +
      'The result must look like an expensive catalogue shoot of this exact toy, not like a cleaned-up phone photo.',
  },
  {
    id: 'restage', label: 'Yeniden çek', temp: 0.8, hint: 'En serbest — en iyi açı ve kompozisyonu kendi seçer',
    prompt:
      'TREATMENT — FULL ART DIRECTION. The reference image defines ONLY the product\'s identity, nothing about the ' +
      'photograph. Ignore the reference\'s angle, pose, lighting, framing and quality completely. ' +
      'Art-direct a brand-new hero shot from scratch: choose the camera angle, height, lens and composition that show ' +
      'this toy at its most appealing and most legible, stage it in its most natural and attractive resting position, ' +
      'and light it like a premium catalogue cover. Deliver a pristine, brand-new retail unit — no dust, no smudges, no ' +
      'stray marks, no background clutter — rendered tack-sharp at high resolution with flawless studio craft.',
  },
];

// ── Kamera açısı ────────────────────────────────────────────────────────────
const ANGLES = [
  { id: 'auto', label: 'Otomatik', prompt: '' },
  {
    id: 'hero34', label: '3/4 hero',
    prompt: 'CAMERA: a slightly elevated three-quarter hero angle, about 30–40° above the product and turned ~35° to one ' +
      'side, so the front face, the top and the depth of the toy all read clearly in one shot.',
  },
  {
    id: 'front', label: 'Tam önden',
    prompt: 'CAMERA: a straight-on, perfectly level eye-height frontal view of the product, square to the camera with no ' +
      'tilt and no perspective distortion — a clean, catalogue-standard front elevation.',
  },
  {
    id: 'top', label: 'Tam üstten',
    prompt: 'CAMERA: a perfectly perpendicular top-down (flat-lay) view, camera directly above the product with no tilt, ' +
      'the toy laid out neatly and squarely to the frame.',
  },
];

const CHILD_ON =
  'HUMAN PRESENCE: you may include a child\'s hands playing with the toy, or a young child partially in frame, to show ' +
  'scale and play value. Keep any person natural, warm and candid; do not let them cover or obscure the product, and ' +
  'keep the entire toy clearly visible. Hands must be anatomically correct with the right number of fingers.';

const CHILD_OFF =
  'HUMAN PRESENCE: absolutely no people, no children, no hands, no body parts and no faces anywhere in the frame. ' +
  'This is a pure product photograph.';

function buildPrompt({ scenePrompt, formatHint, sizeHint, withChild, levelPrompt, anglePrompt }) {
  const parts = [IDENTITY_RULES, levelPrompt];
  if (anglePrompt) parts.push(anglePrompt);
  if (sizeHint) {
    parts.push(
      `PRODUCT NOTE: the product is "${sizeHint}". Use this to get the real-world scale and proportions right — ` +
      'do not add any of this wording as visible text in the image.',
    );
  }
  parts.push(withChild ? CHILD_ON : CHILD_OFF);
  parts.push(`SCENE: ${scenePrompt}`);
  parts.push(`COMPOSITION: ${formatHint}`);
  return parts.filter(Boolean).join('\n\n');
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Görsel yüklenemedi')); };
    img.src = url;
  });
}

// Dosyayı en fazla 1280px kenara indir + base64 payload üret (referans olarak yeterli, hafif).
async function fileToPayload(file) {
  const { img } = await loadImage(file);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const maxEdge = 1280;
  const scale = Math.min(1, maxEdge / Math.max(iw, ih));
  const cw = Math.max(1, Math.round(iw * scale));
  const ch = Math.max(1, Math.round(ih * scale));
  const canvas = document.createElement('canvas');
  canvas.width = cw; canvas.height = ch;
  const ctx = canvas.getContext('2d');
  // beyaz zemin (webp/png şeffaflığı jpeg'de siyah olmasın)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cw, ch);
  ctx.drawImage(img, 0, 0, cw, ch);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  return {
    dataUrl,
    payload: { mimeType: 'image/jpeg', data: dataUrl.split(',')[1] || '' },
    w: iw, h: ih,
  };
}

async function callGeminiScene({ payload, aspect, prompt, model, brandId, temperature = 0.5 }) {
  const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
    body: JSON.stringify({
      model,
      aspectRatio: aspect,
      temperature,
      // image output models can exhaust the default 1024 token budget and return
      // NO image (finishReason MAX_TOKENS) — give them headroom.
      maxOutputTokens: 8192,
      messages: [{ role: 'user', content: prompt, images: [payload] }],
    }),
  });
  const txt = await resp.text();
  let json;
  try { json = JSON.parse(txt); } catch { json = { message: txt }; }
  if (!resp.ok) {
    const d = json?.details;
    const ds = !d ? '' : (typeof d === 'string' ? d.slice(0, 300) : (() => { try { return JSON.stringify(d).slice(0, 300); } catch { return ''; } })());
    throw new Error([json?.error, ds].filter(Boolean).join(' — ') || resp.statusText);
  }
  const images = Array.isArray(json?.images) ? json.images : [];
  if (!images.length) {
    const mt = (json?.message || '').trim();
    throw new Error(mt ? `Model görsel yerine yazı döndü: "${mt.slice(0, 160)}"` : 'Model görsel üretmedi.');
  }
  return images[0]; // { mimeType, data }
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function uid() {
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
}

function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
}

export default function JoyicoStudio() {
  const [sources, setSources] = useState([]); // [{ id, name, dataUrl, payload, w, h }]
  const [selectedFormats, setSelectedFormats] = useState(
    () => FORMATS.filter((f) => f.default).map((f) => f.id),
  );
  const [themeId, setThemeId] = useState('white');
  const [levelId, setLevelId] = useState('improve');
  const [angleId, setAngleId] = useState('auto');
  const [freePrompt, setFreePrompt] = useState('');
  const [sizeHint, setSizeHint] = useState('');
  const [withChild, setWithChild] = useState(false);
  const [variants, setVariants] = useState(1);
  const [model, setModel] = useState('gemini-3-pro-image-preview');
  const [running, setRunning] = useState(false);
  const [zipBuilding, setZipBuilding] = useState(false);
  const [outputs, setOutputs] = useState([]);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') || ''
      );
    } catch (_) { return ''; }
  }, []);

  const activeFormats = useMemo(
    () => FORMATS.filter((f) => selectedFormats.includes(f.id)),
    [selectedFormats],
  );
  const theme = useMemo(() => THEMES.find((t) => t.id === themeId) || THEMES[0], [themeId]);
  const level = useMemo(() => LEVELS.find((l) => l.id === levelId) || LEVELS[1], [levelId]);
  const angle = useMemo(() => ANGLES.find((a) => a.id === angleId) || ANGLES[0], [angleId]);
  const scenePrompt = useMemo(() => {
    const free = freePrompt.trim();
    return free ? `${theme.prompt} ${free}` : theme.prompt;
  }, [theme, freePrompt]);

  const addFiles = useCallback(async (fileList) => {
    setError('');
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    const loaded = [];
    for (const f of files) {
      try {
        const r = await fileToPayload(f);
        loaded.push({ id: uid(), name: f.name.replace(/\.[^.]+$/, ''), ...r });
      } catch (e) {
        setError(`"${f.name}" yüklenemedi: ${e.message || e}`);
      }
    }
    if (loaded.length) setSources((prev) => [...prev, ...loaded]);
  }, []);

  const onFileInput = (e) => { addFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => {
    e.preventDefault(); dragCounter.current = 0; setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };
  const onDragEnter = (e) => { e.preventDefault(); dragCounter.current += 1; setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); dragCounter.current -= 1; if (dragCounter.current <= 0) setIsDragging(false); };

  const removeSource = (id) => setSources((p) => p.filter((s) => s.id !== id));
  const toggleFormat = (id) => setSelectedFormats((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const generate = useCallback(async () => {
    if (running) return;
    if (!sources.length) { setError('Önce en az bir ürün görseli yükle.'); return; }
    if (!activeFormats.length) { setError('En az bir format seç.'); return; }
    setError('');
    setRunning(true);

    // Tüm (görsel × format × varyant) işlerini pending olarak kur
    const jobs = [];
    for (const s of sources) {
      for (const f of activeFormats) {
        for (let v = 1; v <= variants; v++) {
          jobs.push({ id: uid(), srcId: s.id, srcName: s.name, src: s, fmt: f, variant: v });
        }
      }
    }
    setOutputs(jobs.map((j) => ({
      id: j.id, srcId: j.srcId, srcName: j.srcName, formatId: j.fmt.id,
      formatLabel: j.fmt.label, variant: j.variant, aspect: j.fmt.aspect,
      status: 'pending', dataUrl: null, error: null,
    })));

    // Sıralı çalıştır (dev-token rate limitine saygı)
    for (const j of jobs) {
      try {
        const prompt = buildPrompt({
          scenePrompt, formatHint: j.fmt.hint, sizeHint: sizeHint.trim(), withChild,
          levelPrompt: level.prompt, anglePrompt: angle.prompt,
        });
        const img = await callGeminiScene({
          payload: j.src.payload, aspect: j.fmt.aspect, prompt, model, brandId,
          temperature: level.temp ?? 0.5,
        });
        const dataUrl = `data:${img.mimeType || 'image/png'};base64,${img.data}`;
        setOutputs((prev) => prev.map((o) => o.id === j.id
          ? { ...o, status: 'done', dataUrl, mimeType: img.mimeType || 'image/png', data: img.data } : o));
      } catch (e) {
        setOutputs((prev) => prev.map((o) => o.id === j.id
          ? { ...o, status: 'error', error: e.message || String(e) } : o));
      }
    }
    setRunning(false);
  }, [running, sources, activeFormats, variants, scenePrompt, sizeHint, withChild, level, angle, model, brandId]);

  const fileNameFor = useCallback((o) => {
    const ext = (o.mimeType || 'image/png').includes('jpeg') ? 'jpg' : 'png';
    const vs = variants > 1 ? `_v${o.variant}` : '';
    return `${o.srcName}_${theme.id}_${level.id}_${o.formatId}${vs}.${ext}`;
  }, [theme, level, variants]);

  const downloadOne = (o) => {
    if (!o?.dataUrl) return;
    const a = document.createElement('a');
    a.href = o.dataUrl;
    a.download = fileNameFor(o);
    a.click();
  };

  const downloadZip = useCallback(async () => {
    const done = outputs.filter((o) => o.status === 'done' && o.data);
    if (!done.length) return;
    setZipBuilding(true);
    try {
      const files = {};
      const counts = {};
      for (const o of done) {
        const base = fileNameFor(o);
        const ext = base.split('.').pop();
        let name = base;
        counts[name] = (counts[name] || 0) + 1;
        if (counts[name] > 1) name = name.replace(new RegExp(`\\.${ext}$`), `_${counts[name]}.${ext}`);
        files[name] = b64ToBytes(o.data);
      }
      const zipped = await new Promise((resolve, reject) =>
        zip(files, { level: 6 }, (err, data) => (err ? reject(err) : resolve(data))));
      const blob = new Blob([zipped], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `joyico-studio_${theme.id}_${stamp()}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(`ZIP oluşturulamadı: ${e.message || e}`);
    } finally {
      setZipBuilding(false);
    }
  }, [outputs, theme, fileNameFor]);

  const doneCount = outputs.filter((o) => o.status === 'done').length;
  const totalCount = outputs.length;
  const jobCount = sources.length * activeFormats.length * variants;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white">
          <Blocks className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Joyico Ahşap Oyuncak Stüdyosu <span className="text-sm font-normal text-amber-600">· Nano Banana</span>
          </h1>
          <p className="text-sm text-gray-500">
            Ahşap oyuncak görselini yükle → sahne seç → ürünü hiç bozmadan e-ticaret ürün fotoğrafı üret. Varsayılan format <b>3:4</b>.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {/* 1) Ürün görselleri */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-gray-700">1 · Ürün görselleri</div>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={onDrop} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={(e) => e.preventDefault()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-300 hover:border-amber-300 hover:bg-gray-50'}`}
        >
          <Upload className="mb-2 h-7 w-7 text-gray-400" />
          <div className="text-sm font-medium text-gray-600">Görselleri sürükle veya tıkla</div>
          <div className="text-xs text-gray-400">PNG / JPG / WEBP · birden fazla olabilir · net ve tam görünen ürün fotoğrafı en iyi sonucu verir</div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onFileInput} />
        </div>

        {sources.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {sources.map((s) => (
              <div key={s.id} className="group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                <img src={s.dataUrl} alt={s.name} className="h-24 w-full object-contain" />
                <button onClick={() => removeSource(s.id)}
                  className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-gray-500 opacity-0 shadow group-hover:opacity-100 hover:text-red-600">
                  <X className="h-3.5 w-3.5" />
                </button>
                <div className="truncate px-1.5 py-1 text-[10px] text-gray-500">{s.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2) Sahne */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-gray-700">2 · Sahne</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
          {THEMES.map((t) => (
            <button key={t.id} onClick={() => setThemeId(t.id)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition ${themeId === t.id ? 'border-amber-400 bg-amber-50 text-amber-800 ring-1 ring-amber-300' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <span className="text-base">{t.emoji}</span> <span className="truncate">{t.label}</span>
            </button>
          ))}
        </div>

        {/* İyileştirme seviyesi — referansı ne kadar serbest bırakacağı */}
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
          <div className="text-xs font-semibold text-amber-900">İyileştirme seviyesi</div>
          <p className="mt-0.5 text-[11px] text-amber-700">
            Referans fotoğrafın kusurlarını (kötü ışık, eğri duruş, toz, dağınık arka plan) ne kadar düzelteceğini belirler.
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {LEVELS.map((l) => (
              <button key={l.id} onClick={() => setLevelId(l.id)}
                className={`rounded-lg border px-3 py-2 text-left transition ${levelId === l.id ? 'border-amber-500 bg-white text-amber-900 ring-1 ring-amber-400' : 'border-amber-200 bg-white/60 text-gray-600 hover:bg-white'}`}>
                <div className="text-sm font-medium">{l.label}</div>
                <div className="mt-0.5 text-[11px] leading-snug text-gray-500">{l.hint}</div>
              </button>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-amber-900">Kamera açısı</span>
            <div className="inline-flex flex-wrap overflow-hidden rounded-lg border border-amber-200 bg-white text-xs">
              {ANGLES.map((a) => (
                <button key={a.id} onClick={() => setAngleId(a.id)}
                  className={`px-2.5 py-1.5 font-medium ${angleId === a.id ? 'bg-amber-100 text-amber-800' : 'text-gray-500 hover:bg-gray-50'}`}>
                  {a.label}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-gray-500">Otomatik = açıyı model seçer (Sadık'ta referanstaki açı korunur).</span>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-gray-500">Ürün adı / boyutu (opsiyonel ama önerilir)</label>
            <input
              value={sizeHint}
              onChange={(e) => setSizeHint(e.target.value)}
              placeholder="örn. ahşap denge tahtası, yaklaşık 80 cm"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
            />
            <p className="mt-1 text-[11px] text-gray-400">Modelin ölçeği doğru kurması için. Görsele yazı olarak basılmaz.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Ekstra yönlendirme (opsiyonel)</label>
            <textarea
              value={freePrompt}
              onChange={(e) => setFreePrompt(e.target.value)}
              rows={2}
              placeholder="örn. yanında birkaç ahşap blok olsun, sabah ışığı, arka planda pastel yeşil duvar…"
              className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>
        </div>

        <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <input type="checkbox" checked={withChild} onChange={(e) => setWithChild(e.target.checked)}
            className="h-4 w-4 accent-amber-600" />
          Çocuk eli / model kadraja girebilsin
          <span className="text-[11px] text-gray-400">(kapalıyken sahnede hiç insan olmaz — pazaryeri için önerilir)</span>
        </label>
      </div>

      {/* 3) Format + model + üret */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-gray-700">3 · Format & üretim</div>
        <div className="flex flex-wrap items-center gap-2">
          {FORMATS.map((f) => (
            <button key={f.id} onClick={() => toggleFormat(f.id)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${selectedFormats.includes(f.id) ? 'border-amber-400 bg-amber-50 text-amber-800 ring-1 ring-amber-300' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
          <div className="mx-2 h-6 w-px bg-gray-200" />
          <div className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <span>Varyant</span>
            <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
              {[1, 2, 3].map((n) => (
                <button key={n} onClick={() => setVariants(n)}
                  className={`px-2.5 py-1.5 font-medium ${variants === n ? 'bg-amber-100 text-amber-800' : 'bg-gray-50 text-gray-500'}`}>
                  {n}×
                </button>
              ))}
            </div>
          </div>
          <div className="mx-2 h-6 w-px bg-gray-200" />
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 text-xs">
            <button onClick={() => setModel('gemini-3-pro-image-preview')}
              className={`px-2.5 py-1.5 font-medium ${model === 'gemini-3-pro-image-preview' ? 'bg-amber-100 text-amber-800' : 'bg-gray-50 text-gray-500'}`}>
              Nano Banana Pro
            </button>
            <button onClick={() => setModel('gemini-2.5-flash-image')}
              className={`px-2.5 py-1.5 font-medium ${model === 'gemini-2.5-flash-image' ? 'bg-amber-100 text-amber-800' : 'bg-gray-50 text-gray-500'}`}>
              Flash (hızlı)
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={generate} disabled={running || !sources.length || !activeFormats.length}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {running ? `Üretiliyor… (${doneCount}/${totalCount})` : `Üret (${sources.length} görsel × ${activeFormats.length} format × ${variants} = ${jobCount})`}
          </button>
          {doneCount > 0 && (
            <button onClick={downloadZip} disabled={zipBuilding}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
              {zipBuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              ZIP indir ({doneCount})
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Not: Üretim sıralı çalışır (her görsel-format-varyant ayrı API çağrısı). Ahşap dokusu ve boyalı ince detaylar için <b>Nano Banana Pro</b> önerilir.
          Pazaryerine yüklemeden önce çıktıyı ürünle karşılaştır — model nadiren parça sayısını veya deseni kaydırabilir.
        </p>
      </div>

      {/* Sonuçlar */}
      {outputs.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {outputs.map((o) => (
            <div key={o.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <div className="truncate text-xs font-medium text-gray-600">
                  {o.srcName} · {o.formatLabel}{variants > 1 ? ` · v${o.variant}` : ''}
                </div>
                {o.status === 'done' && (
                  <button onClick={() => downloadOne(o)} className="text-gray-400 hover:text-amber-600"><Download className="h-4 w-4" /></button>
                )}
              </div>
              <div
                className="flex items-center justify-center bg-gray-50"
                style={{ aspectRatio: (o.aspect || '3:4').replace(':', ' / ') }}
              >
                {o.status === 'pending' && <Loader2 className="h-6 w-6 animate-spin text-gray-300" />}
                {o.status === 'error' && (
                  <div className="flex flex-col items-center gap-1 px-4 text-center">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                    <div className="text-[11px] text-red-500">{o.error}</div>
                  </div>
                )}
                {o.status === 'done' && <img src={o.dataUrl} alt={o.formatLabel} className="h-full w-full object-contain" />}
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px]">
                {o.status === 'done' && <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> <span className="text-gray-400">Hazır</span></>}
                {o.status === 'pending' && <span className="text-gray-400">Sırada…</span>}
                {o.status === 'error' && <span className="text-red-400">Hata</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {outputs.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-200 py-12 text-center text-gray-400">
          <ImageIcon className="h-8 w-8" />
          <div className="text-sm">Henüz çıktı yok — görsel yükle, sahne seç ve <b>Üret</b>'e bas.</div>
        </div>
      )}
    </div>
  );
}
